import type { AlternateAddressLike, ManualCreditResubmitAlternate } from "../types/utils";
import type { AddressRow } from "../types/modals";

export function addressKindLabel(current: unknown): string {
  const value = current === undefined || current === null ? "" : String(current);
  if (value === "1") return "Current Address";
  if (value === "2") return "Alternate Address";
  const lower = value.toLowerCase();
  if (lower.includes("alternate")) return "Alternate Address";
  return "Previous Address";
}

export function isTruthyFlag(v: unknown): boolean {
  return v === true || v === 1 || v === "1";
}

export function resolveAddressRowRadioValue(row: AddressRow, index: number): string {
  const raw = row.ra_id ?? row.address_id ?? row.id;
  if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
    return String(raw);
  }
  return `__row_${index}`;
}

function isSyntheticAddressRowValue(value: string): boolean {
  return value.startsWith("__row_");
}

function indexFromSyntheticAddressRowValue(value: string): number {
  return Number.parseInt(value.replace("__row_", ""), 10);
}

function addressLocationKey(row: AddressRow | ManualCreditResubmitAlternate): string {
  return [row.street, row.city, row.state, row.zip_code]
    .map((part) => String(part ?? "").trim().toLowerCase())
    .join("|");
}

function addressRowFingerprint(row: AddressRow): string {
  const raId = row.ra_id ?? row.address_id ?? row.id;
  if (raId !== undefined && raId !== null && String(raId).trim() !== "") {
    return `id:${String(raId).trim()}`;
  }
  return addressLocationKey(row);
}

function listContainsLocation(
  rows: AddressRow[],
  candidate: AddressRow | ManualCreditResubmitAlternate,
): boolean {
  const key = addressLocationKey(candidate);
  if (!key.replace(/\|/g, "").trim()) return false;
  return rows.some((row) => addressLocationKey(row) === key);
}

export function addressRowHasDisplayContent(row: AddressRow): boolean {
  if (row.ra_id != null && String(row.ra_id).trim() !== "") return true;
  if (row.address_id != null && String(row.address_id).trim() !== "") return true;
  if (row.id != null && String(row.id).trim() !== "") return true;
  return [row.house_no, row.street, row.city, row.state, row.zip_code].some(
    (part) => part != null && String(part).trim() !== "",
  );
}

function addressFieldsFromRow(row: AddressRow): ManualCreditResubmitAlternate {
  return {
    street: String(row.street ?? "").trim(),
    city: String(row.city ?? "").trim(),
    state: String(row.state ?? "").trim(),
    zip_code: String(row.zip_code ?? "").trim(),
  };
}

function profileAlternateToAddressRow(profileAlt: AlternateAddressLike): AddressRow {
  const street = String(profileAlt.street ?? profileAlt.street_address ?? "").trim();
  return {
    ra_id: profileAlt.ra_id,
    current_address: "2",
    house_no: profileAlt.house_no,
    street,
    city: String(profileAlt.city ?? "").trim(),
    state: String(profileAlt.state ?? "").trim(),
    zip_code: String(profileAlt.zip_code ?? "").trim(),
  };
}

function manualAlternateToAddressRow(manual: ManualCreditResubmitAlternate): AddressRow {
  return { current_address: "2", ...manual };
}

function appendUniqueAddressRows(target: AddressRow[], source: AddressRow[]): AddressRow[] {
  const merged = [...target];
  const seen = new Set(target.map((row) => addressRowFingerprint(row)));

  for (const row of source) {
    if (!addressRowHasDisplayContent(row)) continue;
    const fingerprint = addressRowFingerprint(row);
    if (seen.has(fingerprint) || listContainsLocation(merged, row)) continue;
    merged.push(row);
    seen.add(fingerprint);
  }

  return merged;
}

function mergeCreditReportAddressOptions(input: {
  apiRows: AddressRow[];
  profileAlternates?: AlternateAddressLike[] | null;
}): AddressRow[] {
  const profileRows = (input.profileAlternates ?? [])
    .filter((row) => {
      const alt = profileAlternateToAddressRow(row);
      return addressRowHasDisplayContent(alt);
    })
    .map(profileAlternateToAddressRow);

  return appendUniqueAddressRows(input.apiRows, profileRows);
}

function deriveInitialSelection(input: {
  addresses: AddressRow[];
  lastUsedAddressId: string | null;
  otherOptionLastUsed: boolean;
  unmatchedHistoryManual: ManualCreditResubmitAlternate | null;
}): {
  addressId: string;
  altStreet: string;
  altCity: string;
  altState: string;
  altZip: string;
} {
  const empty = { addressId: "", altStreet: "", altCity: "", altState: "", altZip: "" };

  if (input.otherOptionLastUsed && input.unmatchedHistoryManual?.street) {
    const manual = input.unmatchedHistoryManual;
    return {
      addressId: "OTHER",
      altStreet: manual.street,
      altCity: manual.city,
      altState: manual.state,
      altZip: manual.zip_code,
    };
  }

  if (input.lastUsedAddressId) {
    const idx = input.addresses.findIndex(
      (row) => row.ra_id != null && String(row.ra_id) === input.lastUsedAddressId,
    );
    if (idx >= 0) {
      return { ...empty, addressId: resolveAddressRowRadioValue(input.addresses[idx]!, idx) };
    }
    return { ...empty, addressId: input.lastUsedAddressId };
  }

  return empty;
}

function parseCreditReportAddressRequestResponse(responseData: unknown): {
  rows: AddressRow[];
  lastUsedRaId: string | null;
  lastUsedManualAddress: ManualCreditResubmitAlternate | null;
} {
  if (Array.isArray(responseData)) {
    return { rows: responseData as AddressRow[], lastUsedRaId: null, lastUsedManualAddress: null };
  }

  if (!responseData || typeof responseData !== "object") {
    return { rows: [], lastUsedRaId: null, lastUsedManualAddress: null };
  }

  const body = responseData as Record<string, unknown>;
  const meta =
    body.meta && typeof body.meta === "object" ? (body.meta as Record<string, unknown>) : {};
  const rowsRaw = body.addresses ?? body.data ?? body.rows ?? [];
  const rows = Array.isArray(rowsRaw) ? (rowsRaw as AddressRow[]) : [];

  const lastUsedRaIdRaw =
    body.last_used_ra_id ??
    body.last_used_address_id ??
    meta.last_used_ra_id ??
    meta.last_used_address_id ??
    null;

  const lastUsedManualRaw =
    body.last_used_manual_address ?? body.last_used_address ?? meta.last_used_manual_address ?? null;

  return {
    rows,
    lastUsedRaId:
      lastUsedRaIdRaw != null && String(lastUsedRaIdRaw).trim() !== ""
        ? String(lastUsedRaIdRaw).trim()
        : null,
    lastUsedManualAddress: normalizeHistoryManualAddress(lastUsedManualRaw),
  };
}

function normalizeHistoryManualAddress(source: unknown): ManualCreditResubmitAlternate | null {
  if (!source || typeof source !== "object") return null;
  const row = source as Record<string, unknown>;
  const street = String(row.street ?? row.street_address ?? row.Street ?? "").trim();
  if (!street) return null;
  return {
    street,
    city: String(row.city ?? row.City ?? "").trim(),
    state: String(row.state ?? row.State ?? row.state_code ?? "").trim(),
    zip_code: String(row.zip_code ?? row.zip ?? row.Zipcode ?? "").trim(),
  };
}

function parseScoreNotes(scoreNotes: unknown): Record<string, unknown> {
  if (!scoreNotes) return {};
  if (typeof scoreNotes === "string") {
    try {
      const parsed = JSON.parse(scoreNotes) as unknown;
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  if (typeof scoreNotes === "object" && !Array.isArray(scoreNotes)) {
    return scoreNotes as Record<string, unknown>;
  }
  return {};
}

function getHitDescription(scoreNotes: unknown): string {
  const notes = parseScoreNotes(scoreNotes);
  return String(
    notes["Hit Description"] ?? notes.hit_description ?? notes["Hit description"] ?? "",
  );
}

function getHitCode(scoreNotes: unknown): string {
  const notes = parseScoreNotes(scoreNotes);
  const raw = notes["Hit Code"] ?? notes.hit_code ?? notes["Hit code"] ?? "";
  return raw == null ? "" : String(raw);
}

function isCreditReportHistoryResubmitLogRow(row: Record<string, unknown>): boolean {
  const desc = getHitDescription(row.score_notes).toLowerCase();
  if (desc.includes("resubmit credit report")) return true;

  const hit = getHitCode(row.score_notes);
  const score = row.score;
  const hasNoScore = score === null || score === undefined || String(score).trim() === "";
  return hasNoScore && hit === "0" && desc.includes("resubmit");
}

function creditHistoryRowTimestamp(row: Record<string, unknown>): number {
  const created = row.created_at ?? row.request_time;
  if (created == null) return 0;
  if (typeof created === "number") return created < 1e12 ? created * 1000 : created;
  const parsed = Date.parse(String(created));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function asHistoryRows(
  creditReportHistory: unknown[] | null | undefined,
): Record<string, unknown>[] {
  return (creditReportHistory ?? []).filter(
    (row): row is Record<string, unknown> => !!row && typeof row === "object" && !Array.isArray(row),
  );
}

function pickLatestRealCreditReportHistoryRow(
  history: Record<string, unknown>[],
): Record<string, unknown> | null {
  const realRows = history.filter((row) => row && !isCreditReportHistoryResubmitLogRow(row));
  if (!realRows.length) return null;

  return realRows.reduce((best, row) =>
    creditHistoryRowTimestamp(row) > creditHistoryRowTimestamp(best) ? row : best,
  );
}

function pickAddressIdFromNotes(notes: Record<string, unknown>): string | null {
  const raw =
    notes.ra_id ??
    notes.address_id ??
    notes.resident_address_id ??
    notes.last_used_ra_id ??
    notes["RA ID"] ??
    notes["Address ID"] ??
    null;
  if (raw == null || String(raw).trim() === "") return null;
  return String(raw).trim();
}

function stripHtmlToPlainText(value: string): string {
  return String(value || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function matchAddressRowFromHitDescription(
  hitDescription: string,
  rows: AddressRow[],
): AddressRow | null {
  const plain = stripHtmlToPlainText(hitDescription).toLowerCase();
  if (!plain) return null;

  let best: AddressRow | null = null;
  let bestScore = 0;

  for (const row of rows) {
    const street = String(row.street ?? "").trim().toLowerCase();
    const zip = String(row.zip_code ?? "").trim().toLowerCase();
    let score = 0;

    if (street.length >= 4 && plain.includes(street)) score += 3;
    if (zip.length >= 5 && plain.includes(zip)) score += 2;
    const city = String(row.city ?? "").trim().toLowerCase();
    if (city.length >= 3 && plain.includes(city)) score += 1;

    if (score > bestScore) {
      bestScore = score;
      best = row;
    }
  }

  return bestScore >= 3 ? best : null;
}

function deepFindAddressHintsInValue(
  value: unknown,
  visited: unknown[],
): { raId: string | null; manual: ManualCreditResubmitAlternate | null } {
  if (!value) return { raId: null, manual: null };

  if (typeof value === "object" && !Array.isArray(value)) {
    if (visited.includes(value)) return { raId: null, manual: null };
    visited.push(value);

    const raId = pickAddressIdFromNotes(value as Record<string, unknown>);
    if (raId) return { raId, manual: null };

    const manual = normalizeHistoryManualAddress(value);
    if (manual) return { raId: null, manual };

    for (const key of Object.keys(value as Record<string, unknown>)) {
      const found = deepFindAddressHintsInValue(
        (value as Record<string, unknown>)[key],
        visited,
      );
      if (found.raId || found.manual) return found;
    }
    return { raId: null, manual: null };
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = deepFindAddressHintsInValue(item, visited);
      if (found.raId || found.manual) return found;
    }
  }

  return { raId: null, manual: null };
}

function extractAddressHintsFromHistoryRow(row: Record<string, unknown>): {
  raId: string | null;
  manual: ManualCreditResubmitAlternate | null;
} {
  const notes = parseScoreNotes(row.score_notes);
  const rowLevelId =
    row.ra_id ?? row.address_id ?? row.resident_address_id ?? row.last_used_ra_id;
  const notesId = pickAddressIdFromNotes(notes);
  const deepHints = deepFindAddressHintsInValue(notes, []);
  const rawId = rowLevelId ?? notesId ?? deepHints.raId;
  if (rawId != null && String(rawId).trim() !== "") {
    return { raId: String(rawId).trim(), manual: null };
  }

  const manual =
    normalizeHistoryManualAddress(notes) ??
    normalizeHistoryManualAddress(row) ??
    deepHints.manual;
  if (manual) return { raId: null, manual };

  return { raId: null, manual: null };
}

function pickNearbyResubmitLogRows(
  history: Record<string, unknown>[],
  anchorTimestamp: number,
  windowMs: number,
): Record<string, unknown>[] {
  return history
    .filter((row) => {
      if (!row || !isCreditReportHistoryResubmitLogRow(row)) return false;
      const ts = creditHistoryRowTimestamp(row);
      return Math.abs(ts - anchorTimestamp) <= windowMs;
    })
    .sort(
      (a, b) =>
        Math.abs(creditHistoryRowTimestamp(a) - anchorTimestamp) -
        Math.abs(creditHistoryRowTimestamp(b) - anchorTimestamp),
    );
}

function extractAddressHintsFromNearbyResubmitLogs(
  history: Record<string, unknown>[],
  anchorRow: Record<string, unknown>,
): { raId: string | null; manual: ManualCreditResubmitAlternate | null } {
  const anchorTs = creditHistoryRowTimestamp(anchorRow);
  for (const row of pickNearbyResubmitLogRows(history, anchorTs, 15000)) {
    const hints = extractAddressHintsFromHistoryRow(row);
    if (hints.raId || hints.manual) return hints;
  }
  return { raId: null, manual: null };
}

function extractLastUsedAddressHintsFromCreditHistory(
  addressRows: AddressRow[],
  creditReportHistory: unknown[] | null | undefined,
): {
  lastUsedRaId: string | null;
  lastUsedManualAddress: ManualCreditResubmitAlternate | null;
} {
  const historyRows = asHistoryRows(creditReportHistory);
  const row = pickLatestRealCreditReportHistoryRow(historyRows);
  if (!row) return { lastUsedRaId: null, lastUsedManualAddress: null };

  const directHints = extractAddressHintsFromHistoryRow(row);
  if (directHints.raId) return { lastUsedRaId: directHints.raId, lastUsedManualAddress: null };
  if (directHints.manual) return { lastUsedRaId: null, lastUsedManualAddress: directHints.manual };

  const resubmitHints = extractAddressHintsFromNearbyResubmitLogs(historyRows, row);
  if (resubmitHints.raId) return { lastUsedRaId: resubmitHints.raId, lastUsedManualAddress: null };
  if (resubmitHints.manual) {
    return { lastUsedRaId: null, lastUsedManualAddress: resubmitHints.manual };
  }

  const hitDescription = getHitDescription(row.score_notes);
  const matchedRow = matchAddressRowFromHitDescription(hitDescription, addressRows);
  if (matchedRow?.ra_id != null) {
    return { lastUsedRaId: String(matchedRow.ra_id), lastUsedManualAddress: null };
  }

  return { lastUsedRaId: null, lastUsedManualAddress: null };
}

function collectManualAlternatesFromCreditHistory(
  creditReportHistory: unknown[] | null | undefined,
): ManualCreditResubmitAlternate[] {
  const sorted = [...asHistoryRows(creditReportHistory)].sort(
    (a, b) => creditHistoryRowTimestamp(b) - creditHistoryRowTimestamp(a),
  );
  const seen = new Set<string>();
  const result: ManualCreditResubmitAlternate[] = [];

  for (const row of sorted) {
    const hints = extractAddressHintsFromHistoryRow(row);
    if (!hints.manual?.street) continue;
    const locationKey = addressLocationKey(hints.manual);
    if (!locationKey.replace(/\|/g, "").trim() || seen.has(locationKey)) continue;
    seen.add(locationKey);
    result.push(hints.manual);
  }

  return result;
}

function mergeHistoryManualAlternatesList(
  rows: AddressRow[],
  historyManuals: ManualCreditResubmitAlternate[],
): AddressRow[] {
  const merged = [...rows];
  for (const manual of historyManuals) {
    if (!manual.street || listContainsLocation(merged, manual)) continue;
    merged.push(manualAlternateToAddressRow(manual));
  }
  return merged;
}

function applyLastUsedAddressHints(
  addressList: AddressRow[],
  hints: { lastUsedRaId: string | null; lastUsedManualAddress: ManualCreditResubmitAlternate | null },
): {
  addresses: AddressRow[];
  lastUsedAddressId: string | null;
  otherOptionLastUsed: boolean;
  unmatchedHistoryManual: ManualCreditResubmitAlternate | null;
} {
  const emptyResult = {
    addresses: addressList,
    lastUsedAddressId: null as string | null,
    otherOptionLastUsed: false,
    unmatchedHistoryManual: null as ManualCreditResubmitAlternate | null,
  };

  if (!addressList.length) return emptyResult;

  const flagged = addressList.find((address) => isTruthyFlag(address.last_used));
  if (flagged) {
    return {
      ...emptyResult,
      lastUsedAddressId: flagged.ra_id != null ? String(flagged.ra_id) : null,
    };
  }

  const { lastUsedRaId: targetRaId, lastUsedManualAddress: targetManual } = hints;
  if (!targetRaId && !targetManual?.street) return emptyResult;

  if (targetRaId) {
    return {
      addresses: addressList.map((address) =>
        String(address.ra_id) === targetRaId ? { ...address, last_used: 1 } : address,
      ),
      lastUsedAddressId: targetRaId,
      otherOptionLastUsed: false,
      unmatchedHistoryManual: null,
    };
  }

  let matched = false;
  const addresses = addressList.map((address) => {
    if (!listContainsLocation([address], targetManual!)) return address;
    matched = true;
    return { ...address, last_used: 1 };
  });

  if (matched) {
    const marked = addresses.find((address) => isTruthyFlag(address.last_used));
    return {
      addresses,
      lastUsedAddressId: marked?.ra_id != null ? String(marked.ra_id) : null,
      otherOptionLastUsed: false,
      unmatchedHistoryManual: null,
    };
  }

  return {
    addresses,
    lastUsedAddressId: null,
    otherOptionLastUsed: true,
    unmatchedHistoryManual: targetManual!,
  };
}

export function buildCreditReportAddressModalState(input: {
  apiResponse: unknown;
  profileAlternates?: AlternateAddressLike[] | null;
  creditReportHistory?: unknown[] | null;
}): {
  addresses: AddressRow[];
  addressId: string;
  altStreet: string;
  altCity: string;
  altState: string;
  altZip: string;
} {
  const parsed = parseCreditReportAddressRequestResponse(input.apiResponse);
  let merged = mergeCreditReportAddressOptions({
    apiRows: parsed.rows,
    profileAlternates: input.profileAlternates,
  });
  merged = mergeHistoryManualAlternatesList(
    merged,
    collectManualAlternatesFromCreditHistory(input.creditReportHistory),
  );

  const historyHints = extractLastUsedAddressHintsFromCreditHistory(
    merged,
    input.creditReportHistory,
  );
  const resolved =
    parsed.lastUsedRaId || parsed.lastUsedManualAddress?.street
      ? {
          lastUsedRaId: parsed.lastUsedRaId,
          lastUsedManualAddress: parsed.lastUsedManualAddress,
        }
      : historyHints;

  const lastUsed = applyLastUsedAddressHints(merged, resolved);

  return {
    addresses: lastUsed.addresses,
    ...deriveInitialSelection({
      addresses: lastUsed.addresses,
      lastUsedAddressId: lastUsed.lastUsedAddressId,
      otherOptionLastUsed: lastUsed.otherOptionLastUsed,
      unmatchedHistoryManual: lastUsed.unmatchedHistoryManual,
    }),
  };
}

export function buildRegenerateCreditReportParams(input: {
  addressId: string;
  addresses: AddressRow[];
  otherFields: { street: string; city: string; state: string; zip: string };
  skipOption: "" | "skip_ssn" | "skip_dob";
  reportType: string;
}): Record<string, string | undefined> {
  const params: Record<string, string | undefined> = {};
  const { addressId, addresses, otherFields, skipOption, reportType } = input;

  if (addressId === "OTHER") {
    params.address_id = "OTHER";
    params.street = otherFields.street.trim();
    params.city = otherFields.city.trim();
    params.state = otherFields.state.trim();
    params.zip_code = otherFields.zip.trim();
  } else if (addressId && isSyntheticAddressRowValue(addressId)) {
    const row = addresses[indexFromSyntheticAddressRowValue(addressId)];
    if (row) {
      const fields = addressFieldsFromRow(row);
      params.street = fields.street;
      params.city = fields.city;
      params.state = fields.state;
      params.zip_code = fields.zip_code;
    }
  } else if (addressId) {
    params.address_id = addressId;
  }

  if (skipOption === "skip_ssn") params.skip_ssn = "1";
  if (skipOption === "skip_dob") params.skip_dob = "1";
  if (reportType) params.report_type = reportType;

  return params;
}
